import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ArrowRight, Flame, Trophy, MessageCircle, Sparkles, Heart, BookOpen, NotebookPen } from 'lucide-react';

const MOOD_LABELS = ['Awful', 'Low', 'Okay', 'Good', 'Great'];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [moodMsg, setMoodMsg] = useState('');
  const [savingMood, setSavingMood] = useState(false);

  const load = async () => {
    const [s, c, co, prog] = await Promise.all([
      api.get('/progress/stats').then(r => r.data).catch(() => null),
      api.get('/daily-challenge').then(r => r.data).catch(() => null),
      api.get('/courses/headspace').then(r => r.data).catch(() => null),
      api.get('/progress').then(r => r.data).catch(() => ({ completed_lessons: [] })),
    ]);
    setStats(s); setChallenge(c); setCourse(co); setCompleted(prog.completed_lessons || []);
  };
  useEffect(() => { load(); }, []);

  const submitMood = async (m) => {
    setSavingMood(true);
    try {
      await api.post('/mood', { mood: m });
      setMoodMsg(`Logged: ${MOOD_LABELS[m-1]}`);
      setTimeout(() => setMoodMsg(''), 2500);
      load();
    } finally { setSavingMood(false); }
  };

  const nextLesson = (course?.lessons || []).find((l) => !completed.includes(l.id));

  return (
    <div className="kc-container py-8 md:py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Your Headspace</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-1" data-testid="dash-greeting">Welcome, {user?.name?.split(' ')[0] || 'there'}.</h1>
        </div>
        {nextLesson && (
          <Link to={`/learn/${nextLesson.id}`} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors w-fit" data-testid="dash-continue">
            Continue — {nextLesson.title.length > 30 ? nextLesson.title.slice(0,28)+'…' : nextLesson.title} <ArrowRight size={16} />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Progress */}
        <div className="lg:col-span-8 bg-white border border-kc rounded-[var(--kc-radius-md)] p-6 shadow-kc-tight" data-testid="dash-progress-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">HEADSPACE Course</div>
              <div className="font-display uppercase text-2xl md:text-3xl mt-1">{stats?.progress_pct ?? 0}% complete</div>
            </div>
            <Trophy className="text-gold" size={32} />
          </div>
          <div className="mt-4 w-full h-2.5 bg-surface-2 rounded-full overflow-hidden">
            <div className="h-full bg-gold transition-all duration-500" style={{ width: `${stats?.progress_pct || 0}%` }} />
          </div>
          <div className="mt-3 text-sm text-muted-kc">{stats?.completed_lessons || 0} of {stats?.total_lessons || 0} lessons completed</div>
          {stats?.is_complete && (
            <Link to="/certificate" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold" data-testid="dash-cert-link">View your certificate <ArrowRight size={14}/></Link>
          )}
        </div>

        {/* Streak */}
        <div className="lg:col-span-4 bg-ink text-white rounded-[var(--kc-radius-md)] p-6 relative overflow-hidden kc-noise" data-testid="dash-streak-card">
          <div className="absolute inset-0 gold-sheen pointer-events-none" />
          <div className="relative">
            <div className="text-[11px] tracking-[0.18em] uppercase text-white/70 flex items-center gap-1"><Flame size={14}/> Streak</div>
            <div className="font-display text-6xl md:text-7xl mt-2 text-[color:var(--kc-gold)] leading-none" data-testid="dash-streak-count">{stats?.streak_days || 0}</div>
            <div className="text-white/80 text-sm mt-1">{(stats?.streak_days || 0) === 1 ? 'day' : 'days'} in a row</div>
          </div>
        </div>

        {/* Daily Challenge */}
        <div className="lg:col-span-6 bg-white border border-kc rounded-[var(--kc-radius-md)] p-6 relative" data-testid="dash-challenge-card">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gold" />
          <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-muted-kc"><Sparkles size={14} className="text-gold"/> Today's mindset challenge</div>
          <p className="mt-3 font-display uppercase text-xl md:text-2xl leading-tight" data-testid="dash-challenge-text">{challenge?.challenge || '—'}</p>
        </div>

        {/* Mood */}
        <div className="lg:col-span-6 bg-white border border-kc rounded-[var(--kc-radius-md)] p-6" data-testid="dash-mood-card">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-muted-kc"><Heart size={14} className="text-gold"/> How's your headspace?</div>
          <div className="mt-3 grid grid-cols-5 gap-2">
            {[1,2,3,4,5].map((m) => (
              <button key={m} onClick={() => submitMood(m)} disabled={savingMood} className="h-12 rounded-[var(--kc-radius-sm)] border border-kc-strong hover:bg-gold-soft hover:border-gold transition-colors text-sm font-semibold disabled:opacity-50" data-testid={`dash-mood-${m}`}>
                <div className="font-display text-2xl text-gold">{m}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-kc">{MOOD_LABELS[m-1]}</div>
              </button>
            ))}
          </div>
          {moodMsg && <div className="mt-3 text-sm text-[color:var(--kc-success)]" data-testid="mood-msg">{moodMsg}</div>}
        </div>

        {/* AI Coach card */}
        <Link to="/coach" className="lg:col-span-6 bg-ink text-white rounded-[var(--kc-radius-md)] p-6 relative overflow-hidden hover:bg-black transition-colors kc-noise" data-testid="dash-coach-link">
          <div className="absolute inset-0 gold-sheen pointer-events-none" />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-white/70 flex items-center gap-1"><MessageCircle size={14}/> AI Coach</div>
              <div className="font-display uppercase text-3xl md:text-4xl mt-2">Talk to <span className="text-[color:var(--kc-gold)]">Mick's Voice</span></div>
              <div className="text-white/80 text-sm mt-2 max-w-md">Trained on the HEADSPACE principles. Honest, no-fluff coaching whenever you need it.</div>
            </div>
            <ArrowRight />
          </div>
        </Link>

        {/* Journal card (now Resources) */}
        <Link to="/resources" className="lg:col-span-6 bg-white border border-kc rounded-[var(--kc-radius-md)] p-6 hover:shadow-kc transition-shadow" data-testid="dash-resources-link">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc flex items-center gap-1"><NotebookPen size={14}/> Resources & Journal</div>
              <div className="font-display uppercase text-3xl md:text-4xl mt-2">Reflect.<br/><span className="text-gold">Rewire.</span></div>
              <div className="text-muted-kc text-sm mt-2 max-w-md">Free PDFs, daily audio, recommended books, and a privacy-first journal accountability check-in.</div>
            </div>
            <ArrowRight />
          </div>
        </Link>

        {/* Badges */}
        <div className="lg:col-span-12 bg-white border border-kc rounded-[var(--kc-radius-md)] p-6" data-testid="dash-badges">
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc mb-3">Badges</div>
          {(stats?.badges?.length ?? 0) === 0 ? (
            <div className="text-muted-kc text-sm">Complete your first lesson to earn your First Step badge.</div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {stats.badges.map((b) => (
                <div key={b.slug} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gold-soft border border-gold text-ink text-sm font-semibold" data-testid={`dash-badge-${b.slug}`}>
                  <Trophy size={14} className="text-gold"/> {b.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
